/**
 * The three-marks provenance card (scenarios doc §4) — shown with a rendered
 * generation so a bank developer can see, per decision class, WHO decided:
 * their document, a verified standard, or the model's guess. The guess mark
 * is the most important one; hiding it is what erodes trust.
 *
 * §7 extensions (2026-08-09):
 *   - Requirement realization (M7 on screen): which declared requirements
 *     structurally survived into the output and which evaporated. Structural
 *     presence only — the caveat line says so, per the M7 pre-registration.
 *   - Spec-field provenance seat: renders user-stated / agent-proposed /
 *     library-default per spec field once Task B7 records it; silent until
 *     the data exists (never an empty overclaim).
 */
import type {
  GenerationProvenanceView,
  SpecFieldProvenanceView,
} from '@/features/chat/services/generation-provenance'
import type { RequirementRealizationView } from '@/features/chat/services/requirement-realization'

const SOURCE_STRAND_TEXT_CLASS = 'text-[hsl(var(--brand-source-strong))]'

function MarkSection({
  mark,
  tone,
  items,
}: {
  mark: string
  tone: 'declared' | 'standard' | 'guessed'
  items: string[]
}) {
  if (items.length === 0) return null
  const toneClass =
    tone === 'declared'
      ? SOURCE_STRAND_TEXT_CLASS
      : tone === 'standard'
        ? 'text-sky-700 dark:text-sky-400'
        : 'text-amber-700 dark:text-amber-400'
  return (
    <div className="flex flex-col gap-1">
      <div className={`text-xs font-semibold ${toneClass}`}>{mark}</div>
      <ul className="flex flex-col gap-0.5">
        {items.map((item) => (
          <li key={item} className="text-xs leading-5 text-muted-foreground">
            {item}
          </li>
        ))}
      </ul>
    </div>
  )
}

type SpecFieldOrigin = SpecFieldProvenanceView['entries'][number]['origin']

const ORIGIN_ORDER: readonly SpecFieldOrigin[] = [
  'user-stated',
  'agent-proposed',
  'library-default',
]

const ORIGIN_LABELS: Record<SpecFieldOrigin, { label: string; toneClass: string }> = {
  'user-stated': {
    label: '사용자 선언',
    toneClass: SOURCE_STRAND_TEXT_CLASS,
  },
  'agent-proposed': {
    label: '에이전트 제안',
    toneClass: 'text-amber-700 dark:text-amber-400',
  },
  'library-default': {
    label: '라이브러리 기본값',
    toneClass: 'text-sky-700 dark:text-sky-400',
  },
}

function RequirementRealizationSection({
  realization,
}: {
  realization: RequirementRealizationView
}) {
  const dropped = realization.items.filter((item) => !item.realized)
  return (
    <div
      data-testid="requirement-realization-section"
      className="flex flex-col gap-1 border-t border-border pt-2"
    >
      <div className="text-xs font-semibold text-foreground">
        요청 실현 — {realization.realized} / {realization.total}
      </div>
      <ul className="flex flex-col gap-0.5">
        {realization.items.map((item) => (
          <li
            key={item.id}
            data-realized={item.realized}
            className={`text-xs leading-5 ${
              item.realized
                ? 'text-muted-foreground'
                : 'font-medium text-red-700 dark:text-red-400'
            }`}
          >
            {item.realized ? '✓' : '✗'} {item.label}
            {item.realized ? '' : ' — 산출물에서 확인되지 않았습니다'}
          </li>
        ))}
      </ul>
      <div className="text-[11px] leading-4 text-muted-foreground">
        구조적 실재 여부만 확인한 것으로, 동작 보증이 아닙니다 (M7 계측 기준).
        {dropped.length > 0
          ? ' 누락 항목은 요구를 다시 말해 주시면 반영합니다.'
          : ''}
      </div>
    </div>
  )
}

function SpecFieldProvenanceSection({
  specProvenance,
}: {
  specProvenance: SpecFieldProvenanceView
}) {
  return (
    <div
      data-testid="spec-field-provenance-section"
      className="flex flex-col gap-1 border-t border-border pt-2"
    >
      <div className="text-xs font-semibold text-foreground">
        spec 필드 출처 — 무엇이 당신의 선언이고 무엇이 추측인지
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {ORIGIN_ORDER.map((origin) =>
          specProvenance.counts[origin] > 0 ? (
            <span key={origin} className={`text-[11px] font-medium ${ORIGIN_LABELS[origin].toneClass}`}>
              {ORIGIN_LABELS[origin].label} {specProvenance.counts[origin]}
            </span>
          ) : null,
        )}
      </div>
      <ul className="flex flex-col gap-0.5">
        {specProvenance.entries.map((entry) => (
          <li
            key={entry.path}
            data-origin={entry.origin}
            className="text-xs leading-5 text-muted-foreground"
          >
            <span className={`font-medium ${ORIGIN_LABELS[entry.origin].toneClass}`}>
              [{ORIGIN_LABELS[entry.origin].label}]
            </span>{' '}
            {entry.path}
            {entry.note ? ` — ${entry.note}` : ''}
          </li>
        ))}
      </ul>
    </div>
  )
}

export function GenerationProvenanceCard({
  view,
  realization,
  specProvenance,
}: {
  view: GenerationProvenanceView
  realization?: RequirementRealizationView | null
  specProvenance?: SpecFieldProvenanceView | null
}) {
  return (
    <div
      data-testid="generation-provenance-card"
      className="mb-3 flex flex-col gap-3 rounded-xl border border-border bg-card px-4 py-3"
    >
      <div className="text-sm font-semibold text-foreground">
        이 화면의 결정 근거
      </div>
      <MarkSection mark="선언에서 파생 — 당신이 준 문서가 정했습니다" tone="declared" items={view.declared} />
      <MarkSection mark="표준 기본값 — 검증된 표준이 정했습니다" tone="standard" items={view.standard} />
      <MarkSection mark="제가 골랐습니다 — 문서에 없어서 추측했습니다" tone="guessed" items={view.guessed} />
      {realization ? (
        <RequirementRealizationSection realization={realization} />
      ) : null}
      {specProvenance ? (
        <SpecFieldProvenanceSection specProvenance={specProvenance} />
      ) : null}
      {view.askNext.length > 0 ? (
        <div className="flex flex-col gap-1 border-t border-border pt-2">
          <div className="text-xs font-semibold text-foreground">
            당신에게 물어야 하는 것
          </div>
          <ul className="flex flex-col gap-0.5">
            {view.askNext.map((item) => (
              <li key={item} className="text-xs leading-5 text-muted-foreground">
                · {item}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )
}
